import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, signal, computed, effect, inject, ViewChild, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, takeUntil, switchMap, tap, catchError, of, forkJoin, finalize } from 'rxjs';
import { BreadcrumbService } from 'src/app/shared/service/breadcrumb.service';
import { TabInduxtryComponent } from "src/app/shared/components/common/tabs-induxtry/tab-induxtry/tab-induxtry.component";
import { TabsInduxtryComponent } from 'src/app/shared/components/common/tabs-induxtry/tabs-induxtry.component';
import { DashboardData } from '../../components/dashboard-stats/dashboard-stats.component';
import { AdvancedAnalysisComponent, TemporalDataItem, RuptureModel } from '../../components/advanced-analysis/advanced-analysis.component';
import { DetailConnectedItemService, GetItemJobsFilters, ItemConnected, Job, JobsStatusFilter, JobStatusCounts, Layer, LayerLogsResponse } from '../../services/detail-connected-item.service';
import { LayerSocketService } from '../../services/layer-socket.service';
import { EmptyStateComponent } from 'src/app/shared/components/common/empty-state/empty-state.component';
import { SkeletonDirective } from 'src/app/shared/directives/skeleton/skeleton.directive';
import { ToastrService } from 'ngx-toastr';
import { PipelineRunsComponent } from '../../components/pipeline-runs/pipeline-runs.component';
import type { PipelineRun, PipelineRunsPageChange } from '../../components/pipeline-runs/pipeline-runs.component';
import { STATUS_ICONS, STATUS_LABELS, PipelineExecutionStatus } from '../../models/conexoes.model';
import { AlertsItemDetailsComponent } from '../../components/alerts-item-details/alerts-item-details.component';
import { PipelineStageCardComponent } from '../../components/pipeline-stage-card/pipeline-stage-card.component';
import { IconInduxtryComponent } from 'src/app/shared/components/common/icon-induxtry/icon-induxtry.component';
import { ButtonComponent } from 'src/app/shared/components/inputs/button/button.component';
import { SerieInvestigationComponent } from '../../components/serie-investigation/serie-investigation.component';
import { PipelineLayersConsoleComponent } from '../../components/pipeline-layers-console/pipeline-layers-console.component';
import { MatDialog } from '@angular/material/dialog';
import {
  StartExecutionModalComponent,
  StartExecutionModalResult,
} from '../../components/modals/start-execution-modal/start-execution-modal.component';

type NodeType = 'bronze' | 'silver' | 'gold';

const LAYER_TITLES: Record<NodeType, { title: string; subtitle: string }> = {
  bronze: { title: 'Bronze', subtitle: 'Ingestão de dados' },
  silver: { title: 'Prata', subtitle: 'Filtragem e validação' },
  gold: { title: 'Ouro', subtitle: 'Dados de nível de modelo' },
};
import { BBCE_SOURCE_ID } from '../../models/lakehouse.model';
const DEFAULT_EXECUTIONS_PAGE_SIZE = 15;

const STATUS_QUERY_TO_FILTER: Record<string, JobsStatusFilter> = {
  sucesso: 'completed',
  falha: 'failure',
  em_andamento: 'in_progress',
};

const STATUS_FILTER_TO_QUERY: Record<JobsStatusFilter, string> = {
  completed: 'sucesso',
  failure: 'falha',
  in_progress: 'em_andamento',
};

@Component({
  selector: 'detail-connected-item',
  standalone: true,
  imports: [
    CommonModule,
    TabsInduxtryComponent,
    TabInduxtryComponent,
    PipelineStageCardComponent,
    AdvancedAnalysisComponent,
    EmptyStateComponent,
    SkeletonDirective,
    PipelineRunsComponent,
    AlertsItemDetailsComponent,
    IconInduxtryComponent,
    ButtonComponent,
    SerieInvestigationComponent,
    PipelineLayersConsoleComponent,
  ],
  templateUrl: './detail-connected-item.component.html',
  styleUrls: ['./detail-connected-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailConnectedItemComponent implements OnInit, OnDestroy {
  @ViewChild('detailTabs') detailTabs?: TabsInduxtryComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly detailService = inject(DetailConnectedItemService);
  private readonly layerSocketService = inject(LayerSocketService);
  private readonly destroy$ = new Subject<void>();
  private readonly toastService = inject(ToastrService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  /** Evita múltiplas inscrições ao socket após vários cliques em Executar */
  private hasSubscribedToLayerUpdated = false;

  // Signals reativos
  private readonly routeParams$ = this.route.params.pipe(
    switchMap(params => of(params['id'] || ''))
  );
  readonly itemId = toSignal(this.routeParams$, { initialValue: '' });

  readonly itemConnected = signal<ItemConnected | null>(null);
  readonly jobs = signal<Job[]>([]);
  readonly executions = signal<PipelineRun[]>([]);
  readonly executionsPage = signal(1);
  readonly executionsPageSize = signal(DEFAULT_EXECUTIONS_PAGE_SIZE);
  readonly executionsTotalCount = signal(0);
  readonly isLoadingExecutions = signal(false);
  readonly executionsFilters = signal<GetItemJobsFilters>({});
  readonly executionsStatusCounts = signal<JobStatusCounts>({
    all: 0,
    completed: 0,
    failure: 0,
    in_progress: 0,
  });
  readonly layers = signal<Layer[]>([]);
  readonly selectedVersionId = signal<string>('');
  readonly selectedVersion = signal<Job | null>(null);
  readonly selectedExecution = signal<PipelineRun | null>(null);
  /** Sempre a execução mais recente da série (independente da seleção/filtros). */
  readonly lastExecution = signal<PipelineRun | null>(null);

  readonly statusIconMap = STATUS_ICONS;
  readonly statusLabelMap = STATUS_LABELS;
  readonly selectedNodeType = signal<NodeType>('bronze');
  readonly isLoading = signal<boolean>(false);
  readonly isLoadingTabs = signal<boolean>(false);
  readonly isLoadingLayers = signal<boolean>(false);
  readonly isLoadingExecution = signal<boolean>(false);
  readonly isBBCEDataSetID = signal<boolean>(false);

  /** Dados de dashboard por layerId (para os 3 cards do pipeline) */
  readonly dashboardDataByLayerId = signal<Record<string, DashboardData | null>>({});
  /** Dados da aba Análise avançada (da camada selecionada) */
  readonly advancedAnalysisTemporalData = signal<TemporalDataItem | null>(null);
  readonly advancedAnalysisRupturesData = signal<Record<string, RuptureModel> | null>(null);

  // Cache em memória por (layerId, jobId) para evitar recarregar
  private tabDataCache = new Map<string, {
    dashboardData: DashboardData | null;
    advancedAnalysisTemporalData: TemporalDataItem | null;
    advancedAnalysisRupturesData: Record<string, RuptureModel> | null;
  }>();
  private layersInflightJobId: string | null = null;
  private tabDataInflightJobId: string | null = null;
  private loadedTabDataJobId: string | null = null;
  private readonly layersLoad$ = new Subject<string>();

  private getTabCacheKey(layerId: string, jobId: string): string {
    return `${layerId}::${jobId}`;
  }

  /** Remove do cache os dados das tabs do job para forçar novo carregamento ao receber layer:updated */
  private clearTabCacheForJob(jobId: string): void {
    const keysToDelete: string[] = [];
    this.tabDataCache.forEach((_, key) => {
      if (key.endsWith(`::${jobId}`)) keysToDelete.push(key);
    });
    keysToDelete.forEach(key => this.tabDataCache.delete(key));
  }

  private clearTabCacheForLayer(layerId: string, jobId: string): void {
    this.tabDataCache.delete(this.getTabCacheKey(layerId, jobId));
  }

  private isLayerReadyForTabData(status: string): boolean {
    return status === 'completed';
  }

  private parseLayerUpdatedPayload(payload: unknown): Layer | null {
    if (!payload) return null;
    try {
      const raw = typeof payload === 'string' ? JSON.parse(payload) : payload;
      return raw && typeof raw.job_id === 'string' ? raw as Layer : null;
    } catch {
      return null;
    }
  }

  private parseJobUpdatedPayload(payload: unknown): Job | null {
    if (!payload) return null;
    try {
      const raw = typeof payload === 'string' ? JSON.parse(payload) : payload;
      return raw && typeof raw._id === 'string' ? raw as Job : null;
    } catch {
      return null;
    }
  }

  private parseLogUpdatedPayload(payload: unknown): LayerLogsResponse | null {
    if (!payload) return null;
    try {
      const raw = typeof payload === 'string' ? JSON.parse(payload) : payload;
      return raw && typeof raw.layer_id === 'string' ? raw as LayerLogsResponse : null;
    } catch {
      return null;
    }
  }

  /** Atualiza um job na lista e recalcula executions (para refletir novo status no histórico). */
  private applyJobUpdate(updated: Job): void {
    const isSameItemConnected = updated.serie_id === this.itemConnected()?._id;
    if (!isSameItemConnected) return;
    if (!this.jobMatchesExecutionsFilters(updated)) {
      this.removeJobFromExecutionsList(updated._id);
      return;
    }

    const current = this.jobs();
    const index = current.findIndex(j => j._id === updated._id);
    if (index === -1) {
      if (this.executionsPage() !== 1) {
        this.executionsTotalCount.update(count => count + 1);
        return;
      }
      const newJobs = [updated, ...current].slice(0, this.executionsPageSize());
      this.jobs.set(newJobs);
      this.executions.set(newJobs.map(j => this.mapJobToExecution(j)));
      this.executionsTotalCount.update(count => count + 1);
      this.maybeUpdateLastExecution(updated);
      return;
    }

    const next = [...current];
    next[index] = updated;
    this.jobs.set(next);
    this.executions.set(next.map(j => this.mapJobToExecution(j)));
    if (updated._id === this.selectedVersionId()) {
      this.selectedVersion.set(updated);
      this.selectedExecution.set(this.mapJobToExecution(updated));
    }
    this.maybeUpdateLastExecution(updated);
  }

  private jobMatchesExecutionsFilters(job: Job): boolean {
    const filters = this.executionsFilters();
    if (filters.automated === true && !job.automated) return false;

    const status = filters.status;
    if (!status) return true;
    if (status === 'in_progress') {
      return job.status === 'queued' || job.status === 'initializing' || job.status === 'processing';
    }
    return job.status === status;
  }

  private removeJobFromExecutionsList(jobId: string): void {
    const current = this.jobs();
    const index = current.findIndex(j => j._id === jobId);
    if (index === -1) return;

    const next = current.filter(j => j._id !== jobId);
    this.jobs.set(next);
    this.executions.set(next.map(j => this.mapJobToExecution(j)));
    this.executionsTotalCount.update(count => Math.max(0, count - 1));
  }

  private applyLayerUpdate(updated: Layer): void {
    const currentLayers = this.layers();
    const isJobSelected = updated.job_id === this.selectedVersionId();
    if (!isJobSelected) return;

    const nextLayers = currentLayers.map(layer => {
      if (layer.layer !== updated.layer) return layer;
      return {
        ...layer,
        ...updated,
        _id: updated._id || layer._id,
      } as Layer;
    });
    this.layers.set(nextLayers);

    if (this.isLayerReadyForTabData(updated.status)) {
      const layerId = updated._id || nextLayers.find(layer => layer.layer === updated.layer)?._id;
      if (layerId) {
        this.clearTabCacheForLayer(layerId, updated.job_id);
      }
    }
  }

  private formatVersionDate(isoDate: string): string {
    const defaultFormatter = new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });

    return defaultFormatter.format(new Date(isoDate));
  }
  // Computed signals
  readonly lastExecutionLabel = computed(() => this.lastExecution()?.runNumber ?? '');

  readonly detailsTabTitle = computed(() => {
    const version = this.selectedVersion()?.version;
    return version != null
      ? `Detalhes da execução · ${version}`
      : 'Detalhes da execução';
  });

  readonly selectedLayerId = computed(() => {
    const layers = this.layers();
    const type = this.selectedNodeType();
    return layers?.find(l => l.layer === type)?._id ?? null;
  });
  readonly selectedLayer = computed(() => this.layers()?.find(l => l.layer === this.selectedNodeType()) ?? null);

  readonly selectedLayerDisplayName = computed(() => {
    const titles: Record<NodeType, string> = {
      bronze: 'Ingestão de dados (Bronze)',
      silver: 'Filtragem e validação (Silver)',
      gold: 'Dados de nível de modelo (Gold)',
    };
    return titles[this.selectedNodeType()];
  });

  private toPipelineExecutionStatus(job: Job): PipelineExecutionStatus {
    if (Object.prototype.hasOwnProperty.call(STATUS_ICONS, job.status)) {
      return job.status as PipelineExecutionStatus;
    }
    return 'notDone';
  }

  private mapJobToExecution(job: Job): PipelineRun {
    const formattedDate = this.formatVersionDate(job.createdAt);

    return {
      id: job._id,
      runNumber: formattedDate,
      description: `Execução ${job.version}`,
      status: this.toPipelineExecutionStatus(job),
      automated: job.automated,
      createdAt: job.createdAt,
    };
  }

  private maybeUpdateLastExecution(job: Job): void {
    const mapped = this.mapJobToExecution(job);
    const current = this.lastExecution();
    if (!current) {
      this.lastExecution.set(mapped);
      return;
    }
    if (current.id === mapped.id) {
      this.lastExecution.set(mapped);
      return;
    }
    const currentTime = current.createdAt ? new Date(current.createdAt).getTime() : 0;
    const nextTime = job.createdAt ? new Date(job.createdAt).getTime() : 0;
    if (nextTime >= currentTime || job.version > (this.parseExecutionVersion(current.description) ?? -1)) {
      this.lastExecution.set(mapped);
    }
  }

  private parseExecutionVersion(description: string): number | null {
    const match = /Execução\s+(\d+)/.exec(description);
    return match ? Number(match[1]) : null;
  }

  private syncLastExecutionFromJobs(jobRows: Job[], force = false): void {
    if (!jobRows.length) return;
    const hasActiveFilters = Object.keys(this.executionsFilters()).length > 0;
    if (!force && hasActiveFilters) return;
    this.maybeUpdateLastExecution(jobRows[0]);
  }

  private loadLastExecution(itemId: string): void {
    if (!itemId) return;
    this.detailService.getItemJobs(itemId, 1, 1, {}).pipe(
      takeUntil(this.destroy$),
      tap(jobs => {
        const latest = (jobs?.rows as Job[] | undefined)?.[0];
        if (latest) this.lastExecution.set(this.mapJobToExecution(latest));
      }),
      catchError(() => of(null))
    ).subscribe();
  }

  /** True quando a camada selecionada tem status 'failure'. */
  readonly selectedLayerIsFailed = computed(() => {
    const layers = this.layers();
    const type = this.selectedNodeType();
    const layer = layers?.find(l => l.layer === type);
    return layer?.status === 'failure';
  });

  /** Configuração dos 3 cards do pipeline (ordem: raw, trusted, refined) */
  readonly pipelineStagesConfig = computed(() => {
    const layers = this.layers();
    const selectedType = this.selectedNodeType();
    const byLayerId = this.dashboardDataByLayerId();
    const loading = this.isLoadingTabs();
    if (!layers || layers.length === 0) return [];
    const order: NodeType[] = ['bronze', 'silver', 'gold'];
    return order.map(type => {
      const layer = layers.find(l => l.layer === type);
      if (!layer) return null;
      const info = LAYER_TITLES[type];
      return {
        type,
        layerId: layer._id ?? '',
        steps: layer.steps,
        title: info.title,
        subtitle: info.subtitle,
        status: layer.status,
        dashboardData: layer._id ? (byLayerId[layer._id] ?? null) : null,
        isLoading: loading,
        isSelected: selectedType === type,
      };
    }).filter((c): c is NonNullable<typeof c> => c !== null);
  });

  activeTab = signal<string>('dashboard');
  /** Ao ir para a aba Histórico, abre os logs desta camada (consumido por pipeline-layers-console). */
  focusLogsForLayerId = signal<string | null>(null);

  constructor() {
    this.layersLoad$.pipe(
      switchMap((jobId) =>
        this.detailService.getItemLayersByJobId(jobId).pipe(
          tap((layers) => {
            if (this.selectedVersionId() !== jobId) return;
            if (layers) {
              this.layers.set(layers.rows as Layer[]);
            }
          }),
          catchError((error) => {
            console.error('Erro ao carregar camadas:', error);
            return of(null);
          }),
          finalize(() => {
            if (this.layersInflightJobId === jobId) {
              this.layersInflightJobId = null;
              this.isLoadingLayers.set(false);
            }
            this.isLoading.set(false);
          })
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe();

    // Effect para selecionar automaticamente a última versão quando itemConnected mudar
    effect(() => {
      try {
        const jobs = this.jobs();
        if (jobs && jobs.length > 0 && !this.selectedVersionId()) {
          const lastVersion = jobs[0];
          if (lastVersion?._id) {
            this.selectedVersionId.set(lastVersion._id);
            this.selectedVersion.set(lastVersion);
            this.selectedExecution.set(this.mapJobToExecution(lastVersion));
          }
        }
      } catch (error) {
        console.warn('Erro no effect de seleção de versão:', error);
      }
    });

    // Carrega outputs apenas quando job + layers do mesmo job estiverem prontos.
    // selectedNodeType não entra aqui para não disparar reload ao trocar o card.
    effect(() => {
      try {
        const jobId = this.selectedVersionId();
        const layers = this.layers() || [];
        const layersForJob = layers.filter(layer => layer.job_id === jobId);

        if (!!jobId && layersForJob.length >= 3) {
          untracked(() => this.loadAllLayersData(jobId, layersForJob));
        } else if (jobId && layersForJob.length > 0 && layersForJob.length < 3) {
          untracked(() => {
            this.dashboardDataByLayerId.set({});
            this.advancedAnalysisTemporalData.set(null);
            this.advancedAnalysisRupturesData.set(null);
          });
        }
      } catch (error) {
        console.warn('Erro no effect de carregamento de dados:', error);
      }
    });

    effect(() => {
      const selectedType = this.selectedNodeType();
      const layers = this.layers() || [];
      const jobId = this.selectedVersionId();
      const selectedLayer = layers.find(
        layer => layer.job_id === jobId && layer.layer === selectedType
      ) || null;

      if (selectedLayer?.status === 'failure') {
        untracked(() => {
          this.toastService.show(`A camada selecionada apresentou falha no processamento.`, 'Erro', {
            timeOut: 6000,
            messageClass: 'error'
          });
        });
      }

      untracked(() => this.applySelectedLayerTabData());
    });
  }

  ngOnInit(): void {
    const itemId = this.itemId();
    const filtersFromQuery = this.parseFiltersFromQuery(this.route.snapshot.queryParams);
    this.executionsFilters.set(filtersFromQuery);

    if (itemId) {
      this.loadItemInformation(itemId);
      this.loadJobs(itemId);
      this.loadLastExecution(itemId);
      this.loadExecutionsStatusCounts(itemId, filtersFromQuery.automated === true);
    }
    // Socket ativo ao carregar a tela para receber layer:updated de séries já em execução
    this.connectOnSocket();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carrega informações gerais do item conectado
   */
  private loadItemInformation(itemId: string): void {
    if (!itemId || itemId === '') {
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);

    this.detailService.getItemInformation(itemId).pipe(
      takeUntil(this.destroy$),
      tap(item => {
        if (item) {
          this.itemConnected.set(item);
          this.setBreadcrumb(item);
          const sourceId = sessionStorage.getItem('lastSelectedSource');
          this.isBBCEDataSetID.set(sourceId === BBCE_SOURCE_ID);
        }
      }),
      catchError(error => {
        console.error('Erro ao carregar informações do item:', error);
        this.itemConnected.set(null);
        return of(null);
      }),
      tap(() => this.isLoading.set(false))
    )
      .subscribe();
  }

  /**
   * Carrega a lista de jobs(versões) do item. Se selectJobId for informado, seleciona esse job e carrega suas camadas.
   * Se silent for true, não altera isLoading (atualização oculta, ex.: ao receber layer:updated de job inexistente).
   */
  private loadJobs(itemId: string, selectJobId?: string, silent = false, page = 1): void {
    if (!itemId || itemId === '') {
      if (!silent) this.isLoading.set(false);
      return;
    }

    // Ao alterar o item (e consequentemente os jobs(versões)), limpamos o cache das tabs
    this.tabDataCache.clear();

    if (!silent) this.isLoading.set(true);
    this.executionsPage.set(page);

    this.detailService.getItemJobs(itemId, page, this.executionsPageSize(), this.executionsFilters()).pipe(
      takeUntil(this.destroy$),
      tap(jobs => {
        if (jobs) {
          const jobRows = jobs.rows as Job[];
          this.jobs.set(jobRows);
          const executions = jobRows.map(job => this.mapJobToExecution(job));
          this.executions.set(executions);
          this.executionsTotalCount.set(jobs.count);
          this.loadExecutionsStatusCounts(itemId, this.executionsFilters().automated === true);
          this.syncLastExecutionFromJobs(jobRows, page === 1 && Object.keys(this.executionsFilters()).length === 0);
          const currentSelected = this.selectedVersionId();
          const currentStillInList = jobRows.some(j => j._id === currentSelected);
          const jobIdToSelect =
            selectJobId
            ?? (silent && currentStillInList ? currentSelected : jobRows[0]?._id ?? '');
          this.selectedVersionId.set(jobIdToSelect);
          const selectedJob = jobRows.find(j => j._id === jobIdToSelect) ?? null;
          this.selectedVersion.set(selectedJob);
          this.selectedExecution.set(selectedJob ? this.mapJobToExecution(selectedJob) : null);
          this.getLayers(jobIdToSelect);
        }
      }),
      catchError(error => {
        console.error('Erro ao carregar jobs:', error);
        return of(null);
      }),
      tap(() => { if (!silent) this.isLoading.set(false); })
    )
      .subscribe();
  }

  onExecutionsPageChange({ pageIndex, pageSize }: PipelineRunsPageChange): void {
    const itemId = this.itemId();
    if (!itemId) return;

    const pageSizeChanged = pageSize !== this.executionsPageSize();
    const page = pageSizeChanged ? 1 : pageIndex + 1;

    this.isLoadingExecutions.set(true);
    this.executionsPage.set(page);
    this.executionsPageSize.set(pageSize);

    this.detailService.getItemJobs(itemId, page, pageSize, this.executionsFilters()).pipe(
      takeUntil(this.destroy$),
      tap(jobs => {
        if (jobs) {
          const jobRows = jobs.rows as Job[];
          this.jobs.set(jobRows);
          this.executions.set(jobRows.map(job => this.mapJobToExecution(job)));
          this.executionsTotalCount.set(jobs.count);
          this.syncLastExecutionFromJobs(jobRows, page === 1 && Object.keys(this.executionsFilters()).length === 0);
        }
      }),
      catchError(error => {
        console.error('Erro ao carregar jobs:', error);
        return of(null);
      }),
      tap(() => this.isLoadingExecutions.set(false))
    ).subscribe();
  }

  onExecutionsFiltersChange(filters: GetItemJobsFilters): void {
    const itemId = this.itemId();
    if (!itemId) return;

    this.executionsFilters.set(filters);
    this.executionsPage.set(1);
    this.isLoadingExecutions.set(true);
    this.syncFiltersToQueryParams(filters);
    this.loadExecutionsStatusCounts(itemId, filters.automated === true);

    this.detailService.getItemJobs(itemId, 1, this.executionsPageSize(), filters).pipe(
      takeUntil(this.destroy$),
      tap(jobs => {
        if (jobs) {
          const jobRows = jobs.rows as Job[];
          this.jobs.set(jobRows);
          this.executions.set(jobRows.map(job => this.mapJobToExecution(job)));
          this.executionsTotalCount.set(jobs.count);
        }
      }),
      catchError(error => {
        console.error('Erro ao carregar jobs:', error);
        return of(null);
      }),
      tap(() => this.isLoadingExecutions.set(false))
    ).subscribe();
  }

  private parseFiltersFromQuery(params: Params): GetItemJobsFilters {
    const filters: GetItemJobsFilters = {};
    const statusParam = typeof params['status'] === 'string' ? params['status'] : '';
    const tipoParam = typeof params['tipo'] === 'string' ? params['tipo'] : '';
    const status = STATUS_QUERY_TO_FILTER[statusParam];
    if (status) filters.status = status;
    if (tipoParam === 'auto') filters.automated = true;
    return filters;
  }

  private syncFiltersToQueryParams(filters: GetItemJobsFilters): void {
    const queryParams: Record<string, string | null> = {
      status: filters.status ? STATUS_FILTER_TO_QUERY[filters.status] ?? null : null,
      tipo: filters.automated ? 'auto' : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private loadExecutionsStatusCounts(itemId: string, automated?: boolean): void {
    this.detailService.getItemJobsStatusCounts(itemId, automated).pipe(
      takeUntil(this.destroy$),
      tap(counts => {
        if (counts) this.executionsStatusCounts.set(counts);
      }),
      catchError(error => {
        console.error('Erro ao carregar contagens de jobs:', error);
        return of(null);
      })
    ).subscribe();
  }

  private getLayers(jobId: string): void {
    if (!jobId) return;
    if (this.layersInflightJobId === jobId) return;
    this.layersInflightJobId = jobId;
    this.isLoadingLayers.set(true);
    this.layersLoad$.next(jobId);
  }

  /**
   * Carrega dados das 3 camadas e atualiza dashboard por layerId e análise avançada da camada selecionada
   */
  private loadAllLayersData(jobId: string, layers: Layer[]): void {
    if (this.loadedTabDataJobId === jobId) {
      this.applySelectedLayerTabData();
      return;
    }
    if (this.tabDataInflightJobId === jobId) return;

    const layersForJob = layers.filter(
      layer => layer.job_id === jobId && layer._id && this.isLayerReadyForTabData(layer.status)
    );
    const layerIds = layersForJob.map(l => l._id);
    if (layerIds.length === 0) {
      this.dashboardDataByLayerId.set({});
      this.applySelectedLayerTabData();
      return;
    }

    const cacheKeys = layerIds.map(lid => this.getTabCacheKey(lid, jobId));
    const allCached = cacheKeys.every(key => this.tabDataCache.has(key));
    if (allCached) {
      const byId: Record<string, DashboardData | null> = {};
      layerIds.forEach((lid, i) => {
        const cached = this.tabDataCache.get(cacheKeys[i]);
        byId[lid] = cached?.dashboardData ?? null;
      });
      this.dashboardDataByLayerId.set(byId);
      this.loadedTabDataJobId = jobId;
      this.applySelectedLayerTabData();
      return;
    }

    this.tabDataInflightJobId = jobId;

    this.isLoadingTabs.set(true);
    const requests = layerIds.map(lid => this.detailService.getAllTabData(lid, jobId).pipe(
      catchError(err => {
        console.error('Erro ao carregar dados da camada:', lid, err);
        return of(null);
      })
    ));

    forkJoin(requests).pipe(
      takeUntil(this.destroy$),
      tap(results => {
        if (this.selectedVersionId() !== jobId) return;
        const byId: Record<string, DashboardData | null> = {};
        results.forEach((data, i) => {
          const lid = layerIds[i];
          const dashboard = data && Array.isArray(data.dashboardData) && data.dashboardData.length > 0
            ? data.dashboardData[0]
            : null;
          const temporal = data && Array.isArray(data.advancedAnalysisTemporalData) && data.advancedAnalysisTemporalData.length > 0
            ? data.advancedAnalysisTemporalData[0]
            : null;
          const ruptures = data?.advancedAnalysisRupturesData ?? null;
          byId[lid] = dashboard;
          this.tabDataCache.set(this.getTabCacheKey(lid, jobId), {
            dashboardData: dashboard,
            advancedAnalysisTemporalData: temporal,
            advancedAnalysisRupturesData: ruptures,
          });
        });
        this.dashboardDataByLayerId.set(byId);
        this.loadedTabDataJobId = jobId;
        this.applySelectedLayerTabData();
      }),
      tap(() => this.isLoadingTabs.set(false)),
      finalize(() => {
        if (this.tabDataInflightJobId === jobId) {
          this.tabDataInflightJobId = null;
        }
      })
    ).subscribe();
  }

  /** Aplica dados da aba Análise avançada da camada selecionada a partir do cache */
  private applySelectedLayerTabData(): void {
    const layers = this.layers();
    const selectedType = this.selectedNodeType();
    const jobId = this.selectedVersionId();
    const selectedLayer = layers?.find(l => l.layer === selectedType);
    const layerId = selectedLayer?._id;
    if (!layerId || !jobId) {
      this.advancedAnalysisTemporalData.set(null);
      this.advancedAnalysisRupturesData.set(null);
      return;
    }
    const cached = this.tabDataCache.get(this.getTabCacheKey(layerId, jobId));
    this.advancedAnalysisTemporalData.set(cached?.advancedAnalysisTemporalData ?? null);
    this.advancedAnalysisRupturesData.set(cached?.advancedAnalysisRupturesData ?? null);
  }

  /**
   * Handler para seleção de execução na aba Execuções
   */
  onExecutionSelected(execution: PipelineRun): void {
    if (!execution || execution.id === this.selectedVersionId()) {
      return;
    }
    this.selectedVersionId.set(execution.id);
    this.selectedVersion.set(this.jobs().find(job => job._id === execution.id) ?? null);
    this.selectedExecution.set(execution);
    this.layers.set([]);
    this.dashboardDataByLayerId.set({});
    this.advancedAnalysisTemporalData.set(null);
    this.advancedAnalysisRupturesData.set(null);
    this.loadedTabDataJobId = null;
    this.tabDataInflightJobId = null;
    this.getLayers(execution.id);
  }

  /**
   * Handler para clique em um card de estágio do pipeline (seleciona a camada para as abas abaixo)
   */
  onStageCardClick(type: NodeType): void {
    if (this.selectedNodeType() !== type) {
      this.selectedNodeType.set(type);
      this.applySelectedLayerTabData();
    }
  }

  /**
   * Configura o breadcrumb
   */
  private setBreadcrumb(item: ItemConnected): void {
    const breadcrumbs = [
      {
        path: `/conexoes`,
        value: 'Conexões'
      },
      {
        path: `/conexoes`,
        value: item.dataset_name
      },
      {
        path: `/conexoes/detalhes/${item._id}`,
        value: item.name
      }
    ];
    this.breadcrumbService.setBreadcrumb(breadcrumbs);
  }

  /**
   * Chamado ao clicar em "Acompanhar logs" em uma camada (status processing).
   * Muda para a aba Histórico de execuções e sinaliza qual camada abrir no console.
   */
  onFollowLogs(layerId: string): void {
    this.focusLogsForLayerId.set(layerId);
    this.activeTab.set('executions');
    const executionTab = this.detailTabs?.tabs?.toArray().find((tab) => tab.title === 'Histórico de execuções');
    if (executionTab) {
      this.detailTabs?.selectTab(executionTab);
    }
    // Limpa após consumo para não reabrir ao trocar de aba depois
    setTimeout(() => this.focusLogsForLayerId.set(null), 1500);
  }

  onTabsChange(tab: TabInduxtryComponent): void {
    const title = tab?.title ?? '';
    const isExecutionsTab = title === 'Histórico de execuções';
    this.activeTab.set(isExecutionsTab ? 'executions' : 'dashboard');
  }

  /**
   * Abre o modal de período e inicia execução via POST /jobs/serie/batch.
   */
  onStartExecution(): void {
    const itemId = this.itemId();
    if (!itemId) return;

    const dialogRef = this.dialog.open(StartExecutionModalComponent, {
      width: '600px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        title: 'Nova execução',
        description: 'Informe o intervalo de datas para processar esta série.',
        seriesCount: 1,
      },
    });

    dialogRef.afterClosed().subscribe((result: StartExecutionModalResult | undefined) => {
      if (!result) return;
      this.executeSeriesBatch([itemId], result);
    });
  }

  private executeSeriesBatch(serieIds: string[], range: StartExecutionModalResult): void {
    this.isLoadingExecution.set(true);

    this.detailService.startSeriesBatch({
      serie_ids: serieIds,
      ...range,
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (newJob) => {
        const successMessage = range.trigger_etl_dag === false
          ? 'Job registrado sem disparar o pipeline.'
          : 'Execução iniciada com sucesso.';
        this.toastService.show(successMessage, 'Sucesso', {
          timeOut: 6000,
          messageClass: 'success'
        });
        const itemId = this.itemId();
        if (itemId) {
          this.loadJobs(itemId, newJob._id);
        }
        this.isLoadingExecution.set(false);
      },
      error: (err) => {
        const message = err?.error?.message ?? err?.message ?? 'Falha ao iniciar execução.';
        this.toastService.show(message, 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
        this.isLoadingExecution.set(false);
      },
    });
  }

  connectOnSocket(): void {
    if (!this.hasSubscribedToLayerUpdated) {
      this.hasSubscribedToLayerUpdated = true;
      this.layerSocketService.onPipelineEvents().pipe(
        takeUntil(this.destroy$)
      ).subscribe(({ event, payload }) => {
        if (event === 'layer:updated') {
          const parsed = this.parseLayerUpdatedPayload(payload);
          if (!parsed) return;
          this.applyLayerUpdate(parsed);
          this.detailService.emitLayerUpdate(parsed);
        } else if (event === 'job:updated') {
          const parsed = this.parseJobUpdatedPayload(payload);
          if (!parsed) return;
          this.applyJobUpdate(parsed);
        } else if (event === 'layer-log:updated') {
          const parsed = this.parseLogUpdatedPayload(payload);
          if (!parsed) return;
          this.detailService.emitLayerUpdatedEvent(parsed);
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/conexoes']);
  }
}
